import { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { formatElapsedTime } from '../../../lib/utils';
import { fetchSingleExecution, updateSingleExecution } from '../../../modules/executions';
import TestExecutionAccordionHeader from './TestExecutionAccordionHeader';
import TestExecutionAccordionBody from './test-execution-body/TestExecutionAccordionBody';
import { Tooltip } from '@mui/material';

const pendingKeyFor = (executionId) => `pendingExecutionSave:${executionId}`;

const readPending = (id) => {
  try {
    const raw = localStorage.getItem(pendingKeyFor(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writePending = (id, timeMs) => {
  try {
    localStorage.setItem(
      pendingKeyFor(id),
      JSON.stringify({ actualTime: timeMs, updatedAt: Date.now() })
    );
  } catch {}
};

const clearPending = (id) => {
  try {
    localStorage.removeItem(pendingKeyFor(id));
  } catch {}
};

const AUTOSAVE_INTERVAL = 10000;
const INACTIVITY_LIMIT = 5 * 60 * 1000;

const TestExecutionAccordionItem = ({
  executionId,
  statuses,
  defaultStatus,
  isExpanded,
  onToggle,
  fetchExecutionData,
  testcaseData,
  currentExecution,
  singlePage = false,
  isTestCycleCompleted,
  users,
  labels,
}) => {
  const location = useLocation();
  const { addNotification } = useNotification();

  const [executionData, setExecutionData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(currentExecution?.status?.id || null);
  const [data, setData] = useState(testcaseData || []);
  const [timer, setTimer] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(currentExecution?.actual_time || 0);
  const [loading, setLoading] = useState(false);

  const intervalRef = useRef(null);
  const elapsedTimeRef = useRef(elapsedTime);
  const prevExpandedRef = useRef(false);
  const prevPath = useRef(location.pathname);
  const inactivityRef = useRef(null);

  useEffect(() => {
    if (!executionData) {
      setSelectedStatus(currentExecution?.status?.id);
      setElapsedTime(currentExecution?.actual_time || 0);
      if (currentExecution?.association?.testcase) {
        setData(currentExecution.association.testcase);
      }
    }
  }, [currentExecution]);

  useEffect(() => {
    elapsedTimeRef.current = elapsedTime;
  }, [elapsedTime]);
  const flushPendingIfAny = async () => {
    const pending = readPending(executionId);
    if (!pending || typeof pending.actualTime !== 'number' || pending.actualTime <= 0) return;
    try {
      await updateSingleExecution(executionId, {
        execution: { actualTime: pending.actualTime },
      });
      clearPending(executionId);
    } catch {}
  };

  const startTimer = () => {
    if (intervalRef.current) return;
    setTimer(true);
    const startTime = Date.now() - elapsedTimeRef.current;

    const tick = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 1000);

    const autosave = setInterval(() => {
      writePending(executionId, elapsedTimeRef.current);
      flushPendingIfAny();
    }, AUTOSAVE_INTERVAL);

    intervalRef.current = { tick, autosave };
  };

  const stopTimer = async () => {
    setTimer(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current.tick);
      clearInterval(intervalRef.current.autosave);
      intervalRef.current = null;
    }
    writePending(executionId, elapsedTimeRef.current);
    await flushPendingIfAny();
  };

  const handleTimerClick = async () => {
    if (timer) await stopTimer();
    else startTimer();
  };
  const handleDropdownChange = async (statusId) => {
    try {
      await updateSingleExecution(executionId, { execution: { status: statusId } });
      fetchSelectedExecutionDetails(executionId);
      addNotification('success', 'Test execution updated successfully!');
    } catch (e) {
      if (e.data && e.data.error && e.data.error.refresh_required) {
        fetchSelectedExecutionDetails(executionId);
        fetchExecutionData();
        addNotification('danger', e?.message || 'Failed to update test execution.');
      } else {
        addNotification('danger', 'Failed to update test execution.');
      }
    }
  };

  const fetchSelectedExecutionDetails = async (id = executionId) => {
    setLoading(true);
    try {
      const response = await fetchSingleExecution(id);
      setExecutionData(response);
      setSelectedStatus(response?.status?.id);
      setData(response?.association?.testcase || []);
      if (!timer) {
        const pending = readPending(executionId);
        const next = pending?.actualTime ?? response?.actual_time ?? 0;
        setElapsedTime(next);
        elapsedTimeRef.current = next;
      }
    } catch {
      addNotification('danger', 'Failed to fetch execution details.');
    }
    setLoading(false);
  };

  // Fetch details when this item gets expanded
  useEffect(() => {
    const wasExpanded = prevExpandedRef.current;
    if (isExpanded && !wasExpanded) {
      fetchSelectedExecutionDetails(executionId);
    }
    prevExpandedRef.current = isExpanded;
  }, [isExpanded, executionId]);

  // Stop timer on route change
  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      if (timer) {
        writePending(executionId, elapsedTimeRef.current);
        if (intervalRef.current) {
          clearInterval(intervalRef.current.tick);
          clearInterval(intervalRef.current.autosave);
          intervalRef.current = null;
        }
        setTimer(false);
        flushPendingIfAny();
      }
    }
    prevPath.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      if (timer) writePending(executionId, elapsedTimeRef.current);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [timer]);
  useEffect(() => {
    flushPendingIfAny();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current.tick);
        clearInterval(intervalRef.current.autosave);
      }
    };
  }, []);


  const resetInactivityTimer = () => {
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    inactivityRef.current = setTimeout(() => {
      if (timer) {
        stopTimer();
        addNotification('warning', 'Timer stopped due to 5 minutes of inactivity.');
      }
    }, INACTIVITY_LIMIT);
  };

  useEffect(() => {
    const activityHandler = () => resetInactivityTimer();
    window.addEventListener('mousemove', activityHandler);
    window.addEventListener('keydown', activityHandler);
    window.addEventListener('scroll', activityHandler);
    resetInactivityTimer();
    return () => {
      window.removeEventListener('mousemove', activityHandler);
      window.removeEventListener('keydown', activityHandler);
      window.removeEventListener('scroll', activityHandler);
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
    };
  }, [timer]);

  const statusName = data?.status_details?.name;
  const tooltipMessage =
    statusName !== 'Approved' && statusName !== 'Archieved'
      ? 'This test case is not approved.'
      : '';

  return (
    <Tooltip
      title={tooltipMessage}
      arrow
      disableHoverListener={!tooltipMessage}
      placement="top"
    >
      {/* span needed so Tooltip works when Accordion is disabled */}
      <span style={{ display: 'block' }}>
        <Accordion
          expanded={isExpanded}
          onChange={() => onToggle?.(executionId)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              boxShadow: '0 2px 8px rgba(15,23,42,0.08)',
            },
          }}
        >
          {/* AccordionSummary acts as the clickable header row */}
          <AccordionSummary
            // We render our own expand icon inside the header, so hide the default one
            expandIcon={null}
            sx={{
              px: 0,
              py: 0,
              minHeight: 'unset',
              '& .MuiAccordionSummary-content': {
                m: 0,
                width: '100%',
              },
              '&.Mui-expanded': { minHeight: 'unset' },
              cursor: singlePage ? 'default' : 'pointer',
            }}
          >
            <TestExecutionAccordionHeader
              execution={executionData}
              executionId={executionId}
              data={data}
              isExpanded={isExpanded}
              isTestCycleCompleted={isTestCycleCompleted}
              statuses={statuses}
              selectedStatus={selectedStatus}
              defaultStatus={defaultStatus}
              onDropdownChange={handleDropdownChange}
              timer={timer}
              elapsedTime={elapsedTime}
              onTimerClick={handleTimerClick}
              formatElapsedTime={formatElapsedTime}
              fetchSelectedExecutionDetails={fetchSelectedExecutionDetails}
              currentExecution={currentExecution}
              addNotification={addNotification}
              singlePage={singlePage}
              fetchExecutionData={fetchExecutionData}
              users={users}
            />
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider' }}>
            <TestExecutionAccordionBody
              execution={executionData}
              data={data}
              users={users}
              isTestCycleCompleted={isTestCycleCompleted}
              fetchSelectedExecutionDetails={fetchSelectedExecutionDetails}
              elapsedTime={elapsedTime}
              loading={loading}
              labels={labels}
            />
          </AccordionDetails>
        </Accordion>
      </span>
    </Tooltip>
  );
};

export default TestExecutionAccordionItem;
